/**
 * Input validation and paste-sanitization utilities.
 * - Name fields: characters only (letters, space, hyphen, apostrophe), min/max length.
 * - All text fields/areas: block pasting of code (script tags, HTML, javascript:, etc.).
 */

/** Name field: letters, space, hyphen, apostrophe. Min 2, max 100 by default. */
export const NAME_MIN_LENGTH = 2;
export const NAME_MAX_LENGTH = 100;
export const NAME_PATTERN = /^[a-zA-Z\s\-']+$/;

/** Regex to detect dangerous content: script, iframe, object, embed, event handlers, javascript: */
const DANGEROUS_HTML_PATTERNS = [
  /<script\b[\s\S]*?>[\s\S]*?<\/script>/gi,
  /<script\b[\s\S]*?\/>/gi,
  /<iframe\b/gi,
  /<object\b/gi,
  /<embed\b/gi,
  /<form\b/gi,
  /javascript\s*:/gi,
  /vbscript\s*:/gi,
  /data\s*:/gi,
  /on\w+\s*=\s*["'][^"']*["']/gi,
  /on\w+\s*=\s*[^\s>]+/gi,
  /<style\b[\s\S]*?>[\s\S]*?<\/style>/gi,
  /<link\b[\s\S]*?>/gi,
  /<meta\b/gi,
  /<iframe/gi,
];

/**
 * Returns true if the given HTML or text contains code/script or other dangerous content.
 * @param {string} htmlOrText - Clipboard or input content
 * @returns {boolean}
 */
export const containsDangerousCode = (htmlOrText) => {
  if (!htmlOrText || typeof htmlOrText !== 'string') return false;
  const trimmed = htmlOrText.trim();
  if (!trimmed) return false;
  return DANGEROUS_HTML_PATTERNS.some((re) => re.test(trimmed));
};

/**
 * Strip all HTML tags and return plain text (for safe paste into plain inputs).
 * @param {string} html
 * @returns {string}
 */
export const getPlainTextFromHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  const doc = typeof document !== 'undefined' ? document : null;
  if (doc) {
    const div = doc.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }
  return html.replace(/<[^>]*>/g, '');
};

/**
 * Get safe paste content for contentEditable/rich text. If dangerous, returns plain text only.
 * Caller should preventDefault and insert this when dangerous.
 */
export const getSafePasteForRichText = (html, plain) => {
  const content = html || plain || '';
  if (containsDangerousCode(content)) {
    return getPlainTextFromHtml(content) || (typeof plain === 'string' ? plain : '');
  }
  return null;
};

/**
 * Get safe paste content for plain text fields/textarea. If HTML contains code, use plain text only.
 * @param {string} html - text/html from clipboard
 * @param {string} plain - text/plain from clipboard
 * @returns {string}
 */
export const getSafePasteForPlainInput = (html, plain) => {
  const content = html || plain || '';
  if (containsDangerousCode(content)) {
    return getPlainTextFromHtml(content) || (typeof plain === 'string' ? plain : '');
  }
  if (html) {
    return getPlainTextFromHtml(html);
  }
  return typeof plain === 'string' ? plain : '';
};

/** Email format used across event manager contact fields. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PHONE_MIN_DIGITS = 10;
export const PHONE_MAX_DIGITS = 15;

/** Organizer contact fields on Additional Details (optional when empty). */
export const ORGANIZER_NAME_MAX_LENGTH = 30;
export const ORGANIZER_EMAIL_MAX_LENGTH = 40;
export const ORGANIZER_PHONE_MIN_DIGITS = 10;
export const ORGANIZER_PHONE_MAX_DIGITS = 12;
/** Input cap for formatted phone strings (+, spaces, dashes). Digit count validated separately. */
export const ORGANIZER_PHONE_INPUT_MAX_LENGTH = 20;

export const getPhoneDigits = (val) => (val ? String(val).replace(/\D/g, '') : '');

/**
 * Validate email when provided. Empty values are allowed.
 * @param {string} value
 * @returns {string|null} Error message or null when valid/empty
 */
export const validateOptionalEmail = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  if (!EMAIL_REGEX.test(trimmed)) return 'Enter a valid email address';
  return null;
};

/**
 * Validate phone when provided. Empty values are allowed.
 * @param {string} value
 * @returns {string|null} Error message or null when valid/empty
 */
export const validateOptionalPhone = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  const digits = getPhoneDigits(trimmed);
  if (digits.length < PHONE_MIN_DIGITS) {
    return `Phone number must be at least ${PHONE_MIN_DIGITS} digits`;
  }
  if (digits.length > PHONE_MAX_DIGITS) {
    return `Phone number must be at most ${PHONE_MAX_DIGITS} digits`;
  }
  return null;
};

/**
 * Validate name when provided. Empty values are allowed.
 * @param {string} value
 * @returns {string|null} Error message or null when valid/empty
 */
export const validateOptionalName = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;
  if (trimmed.length < NAME_MIN_LENGTH) {
    return `Minimum ${NAME_MIN_LENGTH} characters required.`;
  }
  if (trimmed.length > NAME_MAX_LENGTH) {
    return `Maximum ${NAME_MAX_LENGTH} characters allowed.`;
  }
  if (!NAME_PATTERN.test(trimmed)) {
    return "Only letters, spaces, hyphen (-) and apostrophe (') are allowed.";
  }
  return null;
};

/**
 * Validate organizer contact rows (optional fields — validated only when filled).
 * @param {Array<{ id: string|number, name?: string, email?: string, phone?: string }>} contacts
 * @returns {{ valid: boolean, errors: Record<string|number, { name?: string, email?: string, phone?: string }> }}
 */
export const validateOrganizerContacts = (contacts) => {
  const errors = {};
  let valid = true;

  (contacts || []).forEach((contact) => {
    const rowErrors = {};
    const trimmedName = String(contact.name || '').trim();
    const trimmedEmail = String(contact.email || '').trim();
    const trimmedPhone = String(contact.phone || '').trim();
    const phoneDigits = getPhoneDigits(trimmedPhone);

    if (trimmedName) {
      if (trimmedName.length < NAME_MIN_LENGTH) {
        rowErrors.name = `Minimum ${NAME_MIN_LENGTH} characters required.`;
      } else if (trimmedName.length > ORGANIZER_NAME_MAX_LENGTH) {
        rowErrors.name = `Maximum ${ORGANIZER_NAME_MAX_LENGTH} characters allowed.`;
      } else if (!NAME_PATTERN.test(trimmedName)) {
        rowErrors.name = "Only letters, spaces, hyphen (-) and apostrophe (') are allowed.";
      }
    }

    if (trimmedEmail) {
      if (trimmedEmail.length > ORGANIZER_EMAIL_MAX_LENGTH) {
        rowErrors.email = `Maximum ${ORGANIZER_EMAIL_MAX_LENGTH} characters allowed.`;
      } else if (!EMAIL_REGEX.test(trimmedEmail)) {
        rowErrors.email = 'Enter a valid email address';
      }
    }

    if (trimmedPhone) {
      if (phoneDigits.length < ORGANIZER_PHONE_MIN_DIGITS) {
        rowErrors.phone = `Phone number must be at least ${ORGANIZER_PHONE_MIN_DIGITS} digits`;
      } else if (phoneDigits.length > ORGANIZER_PHONE_MAX_DIGITS) {
        rowErrors.phone = `Phone number must be at most ${ORGANIZER_PHONE_MAX_DIGITS} digits`;
      }
    }

    if (Object.keys(rowErrors).length > 0) {
      valid = false;
      errors[contact.id] = rowErrors;
    }
  });

  return { valid, errors };
};
